/**
 * Finding Disposition History Modal
 * Displays complete audit trail of all disposition changes for a finding
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Timeline,
  Tag,
  Typography,
  Space,
  Card,
  Spin,
  Empty,
  Descriptions,
  Divider
} from 'antd';
import {
  HistoryOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CommentOutlined
} from '@ant-design/icons';
import { getFindingDispositionHistory } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Text, Title, Paragraph } = Typography;

function FindingDispositionHistoryModal({ visible, findingId, onClose }) {
  const [loading, setLoading] = useState(false);
  const [historyData, setHistoryData] = useState(null);

  useEffect(() => {
    if (visible && findingId) {
      fetchHistory();
    }
  }, [visible, findingId]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await getFindingDispositionHistory(findingId);
      setHistoryData(res.data);
    } catch (error) {
      console.error('Failed to fetch disposition history:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const renderTimelineItem = (item) => {
    const getColor = () => {
      if (item.change_type === 'disposition_set' && item.new_status === 'closed') {
        return 'green';
      } else if (item.change_type === 'disposition_set' && item.new_status === 'in_progress') {
        return 'blue';
      } else if (item.change_type === 'status_change') {
        return 'orange';
      } else {
        return 'gray';
      }
    };

    const getIcon = () => {
      if (item.new_status === 'closed') {
        return <CloseCircleOutlined style={{ fontSize: 16 }} />;
      } else if (item.new_status === 'in_progress') {
        return <CheckCircleOutlined style={{ fontSize: 16 }} />;
      } else {
        return <ClockCircleOutlined style={{ fontSize: 16 }} />;
      }
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const date = dayjs(dateStr);
      return (
        <Space>
          <Text type="secondary">{date.format('MMM D, YYYY h:mm A')}</Text>
          <Text type="secondary">({date.fromNow()})</Text>
        </Space>
      );
    };

    return (
      <Timeline.Item
        key={item.id}
        color={getColor()}
        dot={getIcon()}
      >
        <Card size="small" style={{ marginBottom: 8 }}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {/* Header */}
            <div>
              <Space>
                {item.previous_disposition && item.new_disposition && (
                  <>
                    <Tag color="default">{item.previous_disposition}</Tag>
                    <span>→</span>
                    <Tag color="blue">{item.new_disposition}</Tag>
                  </>
                )}
                {!item.previous_disposition && item.new_disposition && (
                  <Tag color="blue">{item.new_disposition}</Tag>
                )}
              </Space>
            </div>

            {/* Status Change */}
            {item.previous_status !== item.new_status && (
              <div>
                <Text type="secondary">Status: </Text>
                <Tag color="default">{item.previous_status || 'none'}</Tag>
                <span>→</span>
                <Tag color={item.new_status === 'closed' ? 'green' : 'blue'}>
                  {item.new_status}
                </Tag>
              </div>
            )}

            {/* Comment */}
            {item.disposition_comment && (
              <div>
                <CommentOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                <Text italic>"{item.disposition_comment}"</Text>
              </div>
            )}

            {/* Wait Until */}
            {item.wait_until_date && (
              <div>
                <ClockCircleOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                <Text>Wait Until: </Text>
                <Tag color="blue">{item.wait_until_reason}</Tag>
                <Text type="secondary">
                  ({dayjs(item.wait_until_date).format('MMM D, YYYY')})
                </Text>
              </div>
            )}

            {/* Disposition Reason */}
            {item.disposition_reason && (
              <div>
                <Text>Reason: </Text>
                <Tag color="orange">{item.disposition_reason}</Tag>
              </div>
            )}

            {/* Metadata */}
            <Divider style={{ margin: '8px 0' }} />

            {/* User and Timestamp */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                <UserOutlined style={{ color: '#999' }} />
                <Text type="secondary">
                  {item.changed_by_name || 'Unknown'}
                  {item.changed_by_email && ` (${item.changed_by_email})`}
                </Text>
              </Space>
              <div>
                {formatDate(item.changed_at)}
              </div>
            </div>

            {/* Bulk Operation Badge */}
            {item.extra_data?.bulk_operation && (
              <Tag color="purple">Bulk Operation</Tag>
            )}
          </Space>
        </Card>
      </Timeline.Item>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <HistoryOutlined />
          Disposition History
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Spin spinning={loading}>
        {historyData && (
          <>
            {/* Current Finding Info */}
            <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f0f2f5' }}>
              <Descriptions size="small" column={2}>
                <Descriptions.Item label="Control Code">
                  <Text strong>{historyData.control_code}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Current Status">
                  <Tag color={historyData.current_status === 'closed' ? 'green' : 'blue'}>
                    {historyData.current_status}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Title" span={2}>
                  {historyData.title}
                </Descriptions.Item>
                {historyData.current_disposition && (
                  <Descriptions.Item label="Current Disposition" span={2}>
                    <Tag color="blue">{historyData.current_disposition}</Tag>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            <Divider>Change History</Divider>

            {/* Timeline */}
            {historyData.history && historyData.history.length > 0 ? (
              <div style={{ maxHeight: '500px', overflowY: 'auto', padding: '8px' }}>
                <Timeline mode="left">
                  {historyData.history.map(item => renderTimelineItem(item))}
                </Timeline>
              </div>
            ) : (
              <Empty
                description="No disposition history available"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </>
        )}
      </Spin>
    </Modal>
  );
}

export default FindingDispositionHistoryModal;
